import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { LogOut, MapPin, Globe, Heart, Eye, Camera, User, Sliders, BookOpen, CheckCircle, Shield, MessageCircle, ArrowUpDown, Compass, Baby, GlassWater, Ban, Award, Calendar, Briefcase, Pencil, Star, Users, Gem, Sparkles, Gift, Copy, Share2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import BottomNav from "../components/BottomNav";
import PhotoExpandModal from "../components/PhotoExpandModal";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { LANGS, COUNTRIES, t } from "../lib/i18n";
import { tgShareLink } from "../lib/telegram";

const WORLDVIEW_OPTIONS = ["", "Традиционное", "Современное", "Духовное", "Открытое", "Пока не решил(а)"];
const EDUCATION_OPTIONS = ["", "Среднее", "Среднее специальное", "Неоконченное высшее", "Высшее", "Магистратура/PhD"];
const CHILDREN_OPTIONS = ["", "Нет", "Есть", "Хочу", "Не хочу", "Пока не решил(а)"];
const ALCOHOL_OPTIONS = ["", "Не пью", "Редко", "По праздникам", "Иногда", "Часто"];
const SMOKING_OPTIONS = ["", "Не курю", "Иногда", "Курю", "Бросаю"];
const SEEKING_OPTIONS = [
  { id: "love", label: "Любовь", icon: Heart },
  { id: "relationship", label: "Отношения", icon: Gem },
  { id: "dates", label: "Свидания", icon: Star },
  { id: "friendship", label: "Дружба", icon: Users },
  { id: "penpal", label: "Переписка", icon: MessageCircle },
  { id: "networking", label: "Общение", icon: Globe },
  { id: "marriage", label: "Семья", icon: Sparkles },
];
const SPOKEN_LANGUAGE_OPTIONS = [
  "Русский", "English", "Қазақша", "Беларуская", "O'zbekcha", "Қыргызча",
  "Deutsch", "Français", "Español", "Italiano", "Português", "中文",
  "日本語", "한국어", "العربية", "Türkçe", "فارسی", "हिन्दी",
];

const PROFILE_TABS = [
  { id: "main", icon: User, label: "Основное" },
  { id: "about", icon: BookOpen, label: "О себе" },
  { id: "settings", icon: Sliders, label: "Настройки" },
];

function localDateString(y, m, d) {
  const mm = String(m).padStart(2, "0");
  const dd = String(d).padStart(2, "0");
  return `${y}-${mm}-${dd}`;
}

function calcProfileCompletion(user) {
  const checks = [
    !!user?.first_name, !!user?.birth_date, !!user?.gender, !!user?.looking_for,
    !!user?.country, !!user?.city, !!user?.language, !!user?.bio,
    !!user?.height_cm, !!user?.zodiac_sign, !!user?.worldview, !!user?.education,
    !!user?.children, (user?.spoken_languages?.length || 0) > 0,
    !!user?.alcohol, !!user?.smoking, !!user?.work_field, !!user?.preferences,
    (user?.photos?.length || 0) > 0,
    user?.location?.coordinates?.[0] !== 0 || user?.location?.coordinates?.[1] !== 0,
  ];
  const done = checks.filter(Boolean).length;
  return Math.round((done / checks.length) * 100);
}

function CompletionRing({ pct }) {
  const r = 28;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <div className="relative w-16 h-16 flex-shrink-0">
      <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r={r} fill="none" stroke="rgba(244,162,97,0.15)" strokeWidth="5" />
        <circle cx="32" cy="32" r={r} fill="none" stroke="url(#grad)" strokeWidth="5" strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" />
        <defs>
          <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#F4A261" />
            <stop offset="100%" stopColor="#E76F51" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="font-[Onest] font-extrabold text-sm">{pct}%</span>
      </div>
    </div>
  );
}

function FieldCard({ icon: Icon, label, children, hint }) {
  return (
    <div className="rounded-2xl bg-white/90 border border-[color:var(--border)] p-4 hover:shadow-sm transition-shadow">
      <div className="flex items-center gap-2.5 mb-2.5">
        <div className="w-7 h-7 rounded-xl bg-[color:var(--peach)]/12 text-[color:var(--peach-deep)] flex items-center justify-center flex-shrink-0">
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div>
          <div className="text-sm font-semibold">{label}</div>
          {hint && <div className="text-[10px] text-[color:var(--ink-soft)]">{hint}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}

export default function Profile() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("main");
  const [bio, setBio] = useState(user?.bio || "");
  const [city, setCity] = useState(user?.city || "");
  const [language, setLanguage] = useState(user?.language || "ru");
  const [heightCm, setHeightCm] = useState(user?.height_cm || "");
  const [worldview, setWorldview] = useState(user?.worldview || "");
  const [education, setEducation] = useState(user?.education || "");
  const [children, setChildren] = useState(user?.children || "");
  const [spokenLanguages, setSpokenLanguages] = useState(user?.spoken_languages || []);
  const [alcohol, setAlcohol] = useState(user?.alcohol || "");
  const [smoking, setSmoking] = useState(user?.smoking || "");
  const [workField, setWorkField] = useState(user?.work_field || "");
  const [preferences, setPreferences] = useState(user?.preferences || "");
  const [seeking, setSeeking] = useState(user?.seeking || []);
  const [showInFeed, setShowInFeed] = useState(user?.show_in_feed ?? true);
  const [showAge, setShowAge] = useState(user?.show_age ?? true);
  const [showLocation, setShowLocation] = useState(user?.show_location ?? true);
  const [showPremiumBadge, setShowPremiumBadge] = useState(user?.show_premium_badge ?? true);
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [birthDate, setBirthDate] = useState(user?.birth_date || "");
  const [birthDateEditedAt, setBirthDateEditedAt] = useState(user?.birth_date_edited_at || null);
  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [firstNameEditedAt, setFirstNameEditedAt] = useState(user?.first_name_edited_at || null);
  const [editingName, setEditingName] = useState(false);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [expandedPhoto, setExpandedPhoto] = useState(false);
  const [expandedPhotoIdx, setExpandedPhotoIdx] = useState(0);
  const [editingBirthDate, setEditingBirthDate] = useState(false);
  const [referral, setReferral] = useState(null);
  const lang = language || user?.language || "ru";
  const PHOTO_SWIPE_THRESHOLD = 50;
  const cooldownRemaining = birthDateEditedAt
    ? 30 - Math.floor((Date.now() - new Date(birthDateEditedAt).getTime()) / 86400000)
    : 0;
  const nameCooldownRemaining = firstNameEditedAt
    ? 30 - Math.floor((Date.now() - new Date(firstNameEditedAt).getTime()) / 86400000)
    : 0;

  const handlePrevPhoto = useCallback(() => {
    setPhotoIdx((i) => (i > 0 ? i - 1 : (user?.photos?.length || 1) - 1));
  }, [user?.photos?.length]);

  const handleNextPhoto = useCallback(() => {
    setPhotoIdx((i) => (i < (user?.photos?.length || 1) - 1 ? i + 1 : 0));
  }, [user?.photos?.length]);

  const handlePhotoExpand = useCallback((idx) => {
    setExpandedPhotoIdx(idx);
    setExpandedPhoto(true);
  }, []);

  const handleExpandedPrev = useCallback(() => {
    setExpandedPhotoIdx((i) => (i > 0 ? i - 1 : (user?.photos?.length || 1) - 1));
  }, [user?.photos?.length]);

  const handleExpandedNext = useCallback(() => {
    setExpandedPhotoIdx((i) => (i < (user?.photos?.length || 1) - 1 ? i + 1 : 0));
  }, [user?.photos?.length]);

  const handleSwipe = useCallback((_, info) => {
    if (info.offset.x < -PHOTO_SWIPE_THRESHOLD) handleNextPhoto();
    if (info.offset.x > PHOTO_SWIPE_THRESHOLD) handlePrevPhoto();
  }, [handleNextPhoto, handlePrevPhoto]);

  const completion = useMemo(() => calcProfileCompletion(user), [user]);

  useEffect(() => {
    setBio(user?.bio || "");
    setCity(user?.city || "");
    setLanguage(user?.language || "ru");
    setHeightCm(user?.height_cm || "");
    setBirthDate(user?.birth_date || "");
    setBirthDateEditedAt(user?.birth_date_edited_at || null);
    setFirstName(user?.first_name || "");
    setFirstNameEditedAt(user?.first_name_edited_at || null);
    setWorldview(user?.worldview || "");
    setEducation(user?.education || "");
    setChildren(user?.children || "");
    setSpokenLanguages(user?.spoken_languages || []);
    setAlcohol(user?.alcohol || "");
    setSmoking(user?.smoking || "");
    setWorkField(user?.work_field || "");
    setPreferences(user?.preferences || "");
    setSeeking(user?.seeking || []);
    setShowInFeed(user?.show_in_feed ?? true);
    setShowAge(user?.show_age ?? true);
    setShowLocation(user?.show_location ?? true);
    setShowPremiumBadge(user?.show_premium_badge ?? true);
  }, [user?.bio, user?.city, user?.language, user?.height_cm, user?.birth_date, user?.birth_date_edited_at, user?.first_name, user?.first_name_edited_at, user?.worldview, user?.education, user?.children, user?.spoken_languages, user?.alcohol, user?.smoking, user?.work_field, user?.preferences, user?.show_in_feed, user?.show_age, user?.show_location, user?.show_premium_badge]);

  useEffect(() => {
    let alive = true;
    api.get("/admin/me")
      .then(() => { if (alive) setIsAdmin(true); })
      .catch(() => { if (alive) setIsAdmin(false); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    api.get("/referral/me")
      .then(({ data }) => { if (alive) setReferral(data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        bio, city, language,
        height_cm: heightCm === "" ? null : Number(heightCm),
        worldview: worldview || null, education: education || null,
        children: children || null, spoken_languages: spokenLanguages,
        alcohol: alcohol || null, smoking: smoking || null,
        work_field: workField || null, preferences, seeking,
        show_in_feed: showInFeed, show_age: showAge, show_location: showLocation,
        show_premium_badge: showPremiumBadge,
      };
      // Include birth_date if changed
      if (birthDate && birthDate !== user?.birth_date) {
        payload.birth_date = birthDate;
      }
      // Include first_name if changed
      if (firstName && firstName !== user?.first_name) {
        payload.first_name = firstName;
      }
      await api.patch("/profile", payload);
      await refresh();
      toast.success(t(lang, "profile_saved"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t(lang, "error_generic"));
    } finally {
      setSaving(false);
    }
  };

  const doLogout = () => { logout(); nav("/", { replace: true }); };

  const photoCount = user?.photos?.length || 0;
  const countryLabel = COUNTRIES.find((c) => c.code === user?.country)?.label;
  const langLabel = LANGS.find((l) => l.code === user?.language);

  return (
    <div className="kupidon-shell safe-top flex flex-col relative overflow-y-auto no-scrollbar pb-28" data-testid="profile-page">
      {/* Decorative gradient */}
      <div className="absolute top-0 left-0 right-0 h-56 bg-gradient-to-b from-[color:var(--peach)]/12 to-transparent pointer-events-none" />

      {/* Photo section — bigger */}
      <div className="relative w-full h-[42vh] flex-shrink-0 overflow-hidden">
        <motion.div
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.3}
          onDragEnd={handleSwipe}
          className="w-full h-full"
          style={{ touchAction: "pan-y" }}
        >
          {user?.photos?.[photoIdx] ? (
            <img
              src={user.photos[photoIdx].data_url}
              alt=""
              className={`w-full h-full object-cover cursor-pointer ${user.photos[photoIdx].blurred ? "photo-blur" : ""}`}
              onClick={() => handlePhotoExpand(photoIdx)}
              draggable={false}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#F4A261] to-[#E76F51] flex items-center justify-center">
              <Camera className="w-16 h-16 text-white/40" />
            </div>
          )}
        </motion.div>
        <div className="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-black/60 via-black/20 to-transparent pointer-events-none" />

        {user?.photos?.length > 1 && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 flex gap-1.5">
            {user.photos.map((_, i) => (
              <button key={i} type="button" onClick={() => setPhotoIdx(i)}
                className={`h-1.5 rounded-full transition-all cursor-pointer ${
                  i === photoIdx ? "w-7 bg-white" : "w-1.5 bg-white/50 hover:bg-white/70"
                }`}
              />
            ))}
          </div>
        )}

        <div className="absolute top-4 right-4 z-10 text-[10px] px-2.5 py-1.5 rounded-full bg-white/20 backdrop-blur-md text-white border border-white/20 inline-flex items-center gap-1">
          <Camera className="w-3 h-3" /> {photoIdx + 1}/{photoCount}
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-5 z-10">
          <div className="font-[Onest] font-extrabold text-3xl text-white leading-tight drop-shadow-sm">
            {user?.first_name}, {user?.age}
          </div>
          <div className="mt-1 text-sm text-white/80 flex items-center gap-1 drop-shadow-sm">
            <MapPin className="w-3.5 h-3.5" />
            {user?.city || t(lang, "city")}
          </div>
          <div className="text-xs text-white/60 mt-0.5 drop-shadow-sm">{countryLabel} · {langLabel?.flag} {langLabel?.label}</div>
        </div>
      </div>

      {/* ===== COMPLETION CARD ===== */}
      <div className="px-5 mt-4 relative z-10">
        <div className="rounded-2xl bg-white/90 border border-[color:var(--border)] p-4 shadow-sm">
          <div className="flex items-center gap-4">
            <CompletionRing pct={completion} />
            <div className="flex-1 min-w-0">
              <div className="font-[Onest] font-bold text-base">Профиль заполнен</div>
              <div className="text-xs text-[color:var(--ink-soft)] mt-0.5">
                {completion < 50 ? "Добавьте больше информации, чтобы получать больше совпадений" :
                 completion < 80 ? "Хорошо! Осталось совсем немного" :
                 "Отлично! Профиль почти идеален"}
              </div>
            </div>
            <button
              onClick={() => nav("/photos")}
              className="px-3 py-2 rounded-xl bg-[color:var(--peach)]/15 border border-[color:var(--peach)]/40 text-[color:var(--peach-deep)] text-xs font-semibold flex items-center gap-1 flex-shrink-0 hover:bg-[color:var(--peach)]/25 transition-colors"
            >
              <Camera className="w-3.5 h-3.5" /> Фото
            </button>
          </div>
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[color:var(--border)]">
            <div className="flex items-center gap-2 text-xs">
              <Star className="w-3.5 h-3.5 text-[color:var(--teal-superlike)]" />
              <span><strong>{user?.superlikes_left ?? 0}</strong> суперлайков</span>
            </div>
            <button
              type="button"
              onClick={() => nav("/premium")}
              className={`text-[10px] font-semibold flex items-center gap-1 rounded-full px-2 py-1 transition-colors ${
                user?.is_premium
                  ? "bg-[color:var(--stars-gold)]/20 text-[color:var(--ink)] hover:bg-[color:var(--stars-gold)]/30"
                  : "text-[color:var(--ink-soft)] hover:text-[color:var(--peach-deep)]"
              }`}
            >
              {user?.is_premium && <Sparkles className="w-3 h-3 text-[color:var(--stars-gold)]" />}
              {user?.is_premium
                ? `Premium до ${user?.premium_until ? new Date(user.premium_until).toLocaleDateString("ru-RU", { day: "numeric", month: "short" }) : ""}`
                : "Бесплатный тариф"}
            </button>
          </div>
        </div>
      </div>

      {/* ===== PREMIUM PROMO BANNER ===== */}
      {!user?.is_premium && (
        <div className="px-5 mt-3 relative z-10">
          <button
            onClick={() => nav("/premium")}
            data-testid="premium-promo-banner"
            className="w-full rounded-2xl bg-gradient-to-r from-[color:var(--peach)] to-[color:var(--peach-deep)] p-4 flex items-center gap-3 text-left shadow-md shadow-[color:var(--peach-deep)]/20 hover:shadow-lg transition-shadow"
          >
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-[Onest] font-bold text-sm text-white">Стать Premium</div>
              <div className="text-[11px] text-white/85 mt-0.5">Больше суперлайков, кто вас лайкнул и другие бонусы</div>
            </div>
            <div className="text-white/80 text-lg flex-shrink-0">›</div>
          </button>
        </div>
      )}

      {/* ===== TAB BAR ===== */}
      <div className="sticky top-0 z-20 px-5 pt-3 pb-2 bg-gradient-to-b from-[color:var(--peach-bg)] via-[color:var(--peach-bg)] to-transparent">
        <div className="flex gap-1.5 p-1 rounded-2xl bg-white/70 backdrop-blur border border-[color:var(--border)]">
          {PROFILE_TABS.map((t2) => (
            <button
              key={t2.id}
              onClick={() => setTab(t2.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold transition-all ${
                tab === t2.id
                  ? "bg-[color:var(--ink)] text-white shadow-sm"
                  : "text-[color:var(--ink-soft)] hover:text-[color:var(--ink)]"
              }`}
            >
              <t2.icon className="w-3.5 h-3.5" />
              {t2.label}
            </button>
          ))}
        </div>
      </div>

      {/* ===== TAB CONTENT ===== */}
      <div className="px-5 mt-3 space-y-3">
        <AnimatePresence mode="wait">
          {tab === "main" && (
            <motion.div key="main" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="space-y-3">
              {/* Name */}
              <FieldCard icon={User} label="Имя">
                {!editingName ? (
                  <>
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium">{user?.first_name || "—"}</div>
                      <button
                        type="button"
                        onClick={() => setEditingName(true)}
                        className="px-3 py-1.5 rounded-xl bg-[color:var(--peach)]/10 text-[color:var(--peach-deep)] text-xs font-semibold hover:bg-[color:var(--peach)]/20 transition-colors"
                      >
                        <Pencil className="w-3 h-3 inline mr-1" />
                        Изменить
                      </button>
                    </div>
                    {nameCooldownRemaining > 0 && (
                      <div className="text-[10px] text-[color:var(--ink-soft)] mt-1.5">Можно будет изменить через {nameCooldownRemaining} дн.</div>
                    )}
                  </>
                ) : (
                  <div className="space-y-2">
                    <input
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value.slice(0, 40))}
                      placeholder="Ваше имя"
                      className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[color:var(--border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)]/40 text-sm"
                    />
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-[color:var(--ink-soft)]">Можно менять 1 раз в 30 дней</span>
                      <button
                        type="button"
                        onClick={() => { setEditingName(false); setFirstName(user?.first_name || ""); }}
                        className="text-xs text-[color:var(--ink-soft)] underline"
                      >
                        Отмена
                      </button>
                    </div>
                  </div>
                )}
              </FieldCard>

              {/* City */}
              <FieldCard icon={MapPin} label="Город" hint="Показывается в вашей карточке">
                <input
                  data-testid="profile-city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Москва / Астана / Ташкент…"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[color:var(--border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)]/40 text-sm"
                />
              </FieldCard>

              {/* Bio */}
              <FieldCard icon={BookOpen} label="О себе" hint="До 300 символов">
                <textarea
                  data-testid="profile-bio"
                  value={bio}
                  onChange={(e) => setBio(e.target.value.slice(0, 300))}
                  rows={3}
                  placeholder="Например: люблю путешествия, кофе, и хорошие книги…"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[color:var(--border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)]/40 text-sm resize-none"
                />
                <div className="text-[10px] text-[color:var(--ink-soft)] mt-1 text-right">{bio.length}/300</div>
              </FieldCard>

              {/* Birth + Zodiac — 3-column picker */}
              <FieldCard icon={Calendar} label="Дата рождения и знак">
                {!editingBirthDate ? (
                  <>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium">
                          {user?.birth_date ? new Date(user.birth_date).toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" }) : "—"}
                        </div>
                        <div className="text-[11px] text-[color:var(--ink-soft)] mt-0.5">
                          {user?.zodiac_sign || "—"} {user?.age ? `· ${user.age} лет` : ""}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setEditingBirthDate(true)}
                        className="px-3 py-1.5 rounded-xl bg-[color:var(--peach)]/10 text-[color:var(--peach-deep)] text-xs font-semibold hover:bg-[color:var(--peach)]/20 transition-colors"
                      >
                        <Pencil className="w-3 h-3 inline mr-1" />
                        Изменить
                      </button>
                    </div>
                    {cooldownRemaining > 0 && (
                      <div className="text-[10px] text-[color:var(--ink-soft)] mt-1.5">Можно будет изменить через {cooldownRemaining} дн.</div>
                    )}
                  </>
                ) : (
                  <div className="space-y-3">
                    {/* 3-column date picker */}
                    <div className="flex gap-2 items-stretch">
                      <div className="flex-1">
                        <div className="text-[9px] uppercase tracking-wider text-[color:var(--ink-soft)] mb-1 text-center font-medium">День</div>
                        <select
                          value={birthDate ? parseInt(birthDate.split("-")[2], 10) : ""}
                          onChange={(e) => {
                            const day = e.target.value;
                            if (!day) return;
                            const cur = birthDate ? new Date(birthDate) : new Date();
                            const month = birthDate ? parseInt(birthDate.split("-")[1], 10) - 1 : cur.getMonth();
                            const year = birthDate ? parseInt(birthDate.split("-")[0], 10) : cur.getFullYear();
                            const d = new Date(year, month, parseInt(day, 10));
                            if (d.getDate() === parseInt(day, 10)) setBirthDate(localDateString(year, month + 1, parseInt(day, 10)));
                          }}
                          className="w-full appearance-none px-2 py-2.5 rounded-xl bg-white border border-[color:var(--border)] text-center text-xs font-semibold text-[color:var(--ink)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)] cursor-pointer"
                        >
                          <option value="">—</option>
                          {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                            <option key={d} value={d}>{d}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex-1">
                        <div className="text-[9px] uppercase tracking-wider text-[color:var(--ink-soft)] mb-1 text-center font-medium">Месяц</div>
                        <select
                          value={birthDate ? parseInt(birthDate.split("-")[1], 10) : ""}
                          onChange={(e) => {
                            const month = e.target.value;
                            if (!month) return;
                            const cur = birthDate ? new Date(birthDate) : new Date();
                            const day = birthDate ? parseInt(birthDate.split("-")[2], 10) : cur.getDate();
                            const year = birthDate ? parseInt(birthDate.split("-")[0], 10) : cur.getFullYear();
                            const d = new Date(year, parseInt(month, 10) - 1, Math.min(day, 28));
                            setBirthDate(localDateString(year, parseInt(month, 10), Math.min(day, 28)));
                          }}
                          className="w-full appearance-none px-1 py-2.5 rounded-xl bg-white border border-[color:var(--border)] text-center text-xs font-semibold text-[color:var(--ink)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)] cursor-pointer"
                        >
                          <option value="">—</option>
                          {[{v:1,l:"Янв"},{v:2,l:"Фев"},{v:3,l:"Мар"},{v:4,l:"Апр"},{v:5,l:"Май"},{v:6,l:"Июн"},{v:7,l:"Июл"},{v:8,l:"Авг"},{v:9,l:"Сен"},{v:10,l:"Окт"},{v:11,l:"Ноя"},{v:12,l:"Дек"}].map((m) => (
                            <option key={m.v} value={m.v}>{m.l}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex-[1.3]">
                        <div className="text-[9px] uppercase tracking-wider text-[color:var(--ink-soft)] mb-1 text-center font-medium">Год</div>
                        <select
                          value={birthDate ? parseInt(birthDate.split("-")[0], 10) : ""}
                          onChange={(e) => {
                            const year = e.target.value;
                            if (!year) return;
                            const cur = birthDate ? new Date(birthDate) : new Date();
                            const day = birthDate ? Math.min(parseInt(birthDate.split("-")[2], 10), 28) : 1;
                            const month = birthDate ? parseInt(birthDate.split("-")[1], 10) - 1 : 0;
                            const d = new Date(parseInt(year, 10), month, day);
                            setBirthDate(localDateString(parseInt(year, 10), month + 1, day));
                          }}
                          className="w-full appearance-none px-1 py-2.5 rounded-xl bg-white border border-[color:var(--border)] text-center text-xs font-semibold text-[color:var(--ink)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)] cursor-pointer"
                        >
                          <option value="">—</option>
                          {Array.from({ length: 83 }, (_, i) => new Date().getFullYear() - 18 - i).map((y) => (
                            <option key={y} value={y}>{y}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-[color:var(--ink-soft)]">Можно менять 1 раз в 30 дней</span>
                      <button
                        type="button"
                        onClick={() => { setEditingBirthDate(false); setBirthDate(user?.birth_date || ""); }}
                        className="text-xs text-[color:var(--ink-soft)] underline"
                      >
                        Отмена
                      </button>
                    </div>
                  </div>
                )}
              </FieldCard>

              {/* Height */}
              <FieldCard icon={ArrowUpDown} label="Рост">
                <input
                  data-testid="profile-height"
                  type="number" min={120} max={230}
                  value={heightCm}
                  onChange={(e) => setHeightCm(e.target.value)}
                  placeholder="170 см"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[color:var(--border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)]/40 text-sm"
                />
              </FieldCard>

              {/* Language */}
              <FieldCard icon={Globe} label="Язык интерфейса">
                <select
                  data-testid="profile-language"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[color:var(--border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)]/40 text-sm appearance-none"
                >
                  {LANGS.map((l) => (
                    <option key={l.code} value={l.code}>{l.flag} {l.label}</option>
                  ))}
                </select>
              </FieldCard>
            </motion.div>
          )}

          {tab === "about" && (
            <motion.div key="about" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="space-y-3">
              <FieldCard icon={Compass} label="Мировоззрение">
                <select value={worldview} onChange={(e) => setWorldview(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[color:var(--border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)]/40 text-sm appearance-none">
                  {WORLDVIEW_OPTIONS.map((v) => (<option key={v} value={v}>{v || "Выбрать"}</option>))}
                </select>
              </FieldCard>

              <FieldCard icon={Award} label="Образование">
                <select value={education} onChange={(e) => setEducation(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[color:var(--border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)]/40 text-sm appearance-none">
                  {EDUCATION_OPTIONS.map((v) => (<option key={v} value={v}>{v || "Выбрать"}</option>))}
                </select>
              </FieldCard>

              <FieldCard icon={Baby} label="Дети">
                <select value={children} onChange={(e) => setChildren(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[color:var(--border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)]/40 text-sm appearance-none">
                  {CHILDREN_OPTIONS.map((v) => (<option key={v} value={v}>{v || "Выбрать"}</option>))}
                </select>
              </FieldCard>

              <FieldCard icon={MessageCircle} label="Языки, которыми владеете">
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {SPOKEN_LANGUAGE_OPTIONS.map((item) => {
                    const active = spokenLanguages.includes(item);
                    return (
                      <button key={item} type="button"
                        onClick={() => setSpokenLanguages((curr) => active ? curr.filter((x) => x !== item) : [...curr, item])}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                          active ? "bg-[color:var(--peach)] text-white border-[color:var(--peach)] shadow-sm" : "bg-white/70 text-[color:var(--ink)] border-[color:var(--border)] hover:bg-white"
                        }`}
                      >{item}</button>
                    );
                  })}
                </div>
              </FieldCard>

              <FieldCard icon={GlassWater} label="Алкоголь">
                <select value={alcohol} onChange={(e) => setAlcohol(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[color:var(--border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)]/40 text-sm appearance-none">
                  {ALCOHOL_OPTIONS.map((v) => (<option key={v} value={v}>{v || "Выбрать"}</option>))}
                </select>
              </FieldCard>

              <FieldCard icon={Ban} label="Курение">
                <select value={smoking} onChange={(e) => setSmoking(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[color:var(--border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)]/40 text-sm appearance-none">
                  {SMOKING_OPTIONS.map((v) => (<option key={v} value={v}>{v || "Выбрать"}</option>))}
                </select>
              </FieldCard>

              <FieldCard icon={Briefcase} label="Сфера работы">
                <input value={workField} onChange={(e) => setWorkField(e.target.value.slice(0, 80))}
                  placeholder="IT, медицина, финансы…"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-[color:var(--border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)]/40 text-sm"
                />
              </FieldCard>

              {/* Seeking — Что ищете */}
              <FieldCard icon={Compass} label="Что ищете" hint="Можно выбрать несколько">
                <div className="flex flex-wrap gap-1.5">
                  {SEEKING_OPTIONS.map((opt) => {
                    const active = seeking.includes(opt.id);
                    return (
                      <button key={opt.id} type="button"
                        onClick={() => setSeeking((curr) => active ? curr.filter((x) => x !== opt.id) : [...curr, opt.id])}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border transition-all ${
                          active ? "bg-[color:var(--peach)] text-white border-[color:var(--peach)] shadow-sm" : "bg-white/70 text-[color:var(--ink)] border-[color:var(--border)] hover:bg-white"
                        }`}
                      >
                        <opt.icon className="w-3.5 h-3.5" />
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </FieldCard>
            </motion.div>
          )}

          {tab === "settings" && (
            <motion.div key="settings" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="space-y-3">
              {/* Visibility */}
              <FieldCard icon={Eye} label="Видимость профиля">
                <div className="space-y-2.5">
                  <label className="flex items-center justify-between text-sm">
                    <span>Показывать меня в ленте</span>
                    <Switch checked={showInFeed} onCheckedChange={setShowInFeed} data-testid="profile-show-in-feed" />
                  </label>
                  <label className="flex items-center justify-between text-sm">
                    <span>Показывать мой возраст</span>
                    <Switch checked={showAge} onCheckedChange={setShowAge} data-testid="profile-show-age" />
                  </label>
                  <label className="flex items-center justify-between text-sm">
                    <span>Показывать геолокацию</span>
                    <Switch checked={showLocation} onCheckedChange={setShowLocation} data-testid="profile-show-location" />
                  </label>
                  {user?.is_premium && (
                    <label className="flex items-center justify-between text-sm">
                      <span>Показывать значок Premium</span>
                      <Switch checked={showPremiumBadge} onCheckedChange={setShowPremiumBadge} data-testid="profile-show-premium-badge" />
                    </label>
                  )}
                </div>
              </FieldCard>

              {/* Referral / invite friends */}
              <FieldCard icon={Gift} label="Пригласить друзей" hint={`+${referral?.reward_days ?? 1} день Premium за каждого друга`}>
                {referral?.link ? (
                  <div className="space-y-2.5">
                    <div className="flex items-center gap-2">
                      <input
                        readOnly
                        value={referral.link}
                        onClick={(e) => e.target.select()}
                        className="flex-1 min-w-0 px-3 py-2.5 rounded-xl bg-white border border-[color:var(--border)] text-xs text-[color:var(--ink-soft)] truncate"
                      />
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(referral.link);
                            toast.success("Ссылка скопирована");
                          } catch {
                            toast.error(t(lang, "error_generic"));
                          }
                        }}
                        className="flex-shrink-0 w-10 h-10 rounded-xl bg-[color:var(--peach)]/15 text-[color:var(--peach-deep)] flex items-center justify-center hover:bg-[color:var(--peach)]/25 transition-colors"
                        aria-label="Копировать"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => tgShareLink(referral.link, "Заходи в Kupidon — знакомства в Telegram!")}
                      className="w-full flex items-center justify-center gap-2 text-sm py-2.5 px-4 rounded-xl bg-[color:var(--peach)]/15 text-[color:var(--peach-deep)] font-semibold hover:bg-[color:var(--peach)]/25 transition-colors"
                    >
                      <Share2 className="w-4 h-4" /> Поделиться
                    </button>
                    <div className="text-[11px] text-[color:var(--ink-soft)] text-center">
                      Приглашено: {referral.referred_count} · Начислено дней: {referral.rewarded_count}
                      {" "}· На этой неделе: {referral.days_this_week}/{referral.weekly_cap_days}
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-[color:var(--ink-soft)]">Загружаем ссылку…</div>
                )}
              </FieldCard>

              {/* Location update */}
              <FieldCard icon={Compass} label="Геолокация" hint="Обновите, чтобы находить людей рядом">
                <button
                  type="button"
                  onClick={() => {
                    if (!navigator.geolocation) { toast.error(t(lang, "geo_unavailable")); return; }
                    navigator.geolocation.getCurrentPosition(
                      async (pos) => {
                        try {
                          await api.post("/profile/location", { latitude: pos.coords.latitude, longitude: pos.coords.longitude });
                          toast.success(t(lang, "profile_location_updated"));
                          await refresh();
                        } catch (e) { toast.error(e.response?.data?.detail || t(lang, "error_generic")); }
                      },
                      () => toast.error(t(lang, "geo_unavailable")),
                      { timeout: 8000 }
                    );
                  }}
                  className="w-full text-sm py-2.5 px-4 rounded-xl bg-[color:var(--peach)]/15 text-[color:var(--peach-deep)] font-semibold hover:bg-[color:var(--peach)]/25 transition-colors"
                >
                  {t(lang, "profile_location_update")}
                </button>
              </FieldCard>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ===== SAVE BUTTON ===== */}
        <motion.button
          data-testid="profile-save"
          onClick={save}
          disabled={saving}
          whileTap={{ scale: 0.98 }}
          className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-[color:var(--ink)] to-[color:var(--ink)] text-white font-semibold text-sm disabled:opacity-50 shadow-lg shadow-[#1F1A17]/15 flex items-center justify-center gap-2"
        >
          {saving ? (
            <><div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" /> Сохраняем…</>
          ) : (
            <><CheckCircle className="w-4 h-4" /> {t(lang, "profile_save")}</>
          )}
        </motion.button>
      </div>

      {/* ===== FOOTER ===== */}
      <div className="px-5 mt-6 mb-4 flex flex-col gap-1">
        {isAdmin && (
          <button onClick={() => nav("/admin")}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-[color:var(--ink-soft)] hover:bg-white/60 transition-colors text-sm"
          >
            <Shield className="w-4 h-4" /> Админ-панель
          </button>
        )}
        <button onClick={doLogout}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-[color:var(--ink-soft)] hover:bg-white/60 transition-colors text-sm"
        >
          <LogOut className="w-4 h-4" /> {t(lang, "logout")}
        </button>
      </div>

      <BottomNav />

      {expandedPhoto && (
        <PhotoExpandModal
          photos={user?.photos || []}
          currentIndex={expandedPhotoIdx}
          onClose={() => setExpandedPhoto(false)}
          onPrev={handleExpandedPrev}
          onNext={handleExpandedNext}
        />
      )}
    </div>
  );
}
